import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { resolveImageUrl } from '../lib/imageUrl';
import { Calendar, Image as ImageIcon, Play } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DailyLifePage() {
  const { t, getLocalizedContent } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState('posts');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [postsRes, galleryRes] = await Promise.all([
        axios.get(`${API}/daily-life`),
        axios.get(`${API}/gallery`)
      ]);
      setPosts(postsRes.data);
      setGallery(galleryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div data-testid="daily-life-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <span className="inline-block px-4 py-1 bg-[#0B6AB3]/20 text-[#0B6AB3] text-sm font-medium rounded-full mb-4">
              Vie d'entreprise
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white font-['Montserrat']">
              {t('daily_life_title')}
            </h1>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto text-lg">
              {t('daily_life_subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-12">
              <TabsList className="bg-[#F8F9FA]" data-testid="daily-life-tabs">
                <TabsTrigger value="posts">Actualités</TabsTrigger>
                <TabsTrigger value="gallery">{t('daily_life_gallery')}</TabsTrigger>
              </TabsList>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div>
              </div>
            ) : (
              <>
                {/* Posts Tab */}
                <TabsContent value="posts">
                  {posts.length === 0 ? (
                    <div className="text-center py-20 text-gray-500">
                      Aucune publication pour le moment
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {posts.map((post, index) => (
                        <motion.div
                          key={post.id}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          variants={fadeInUp}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Card className="h-full border-0 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden" data-testid={`post-card-${index}`}>
                            {post.images?.[0] && (
                              <div className="relative h-48 overflow-hidden">
                                <img
                                  src={resolveImageUrl(post.images[0])}
                                  alt={getLocalizedContent(post.title)}
                                  className="w-full h-full object-cover"
                                />
                                {post.video_url && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                    <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                                      <Play className="h-6 w-6 text-[#0B6AB3] ml-1" />
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            <CardContent className="p-5">
                              <div className="flex items-center text-sm text-gray-500 mb-3">
                                <Calendar className="h-4 w-4 mr-1" />
                                {post.publish_date ? new Date(post.publish_date).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                }) : 'Récent'}
                              </div>
                              <h3 className="font-semibold text-lg text-[#0F172A] mb-2 line-clamp-2">
                                {getLocalizedContent(post.title)}
                              </h3>
                              <p className="text-gray-600 text-sm line-clamp-3">
                                {getLocalizedContent(post.content)}
                              </p>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Gallery Tab */}
                <TabsContent value="gallery">
                  {gallery.length === 0 ? (
                    <div className="text-center py-20 text-gray-500">
                      <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p>Aucune image dans la galerie pour le moment</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {gallery.map((image, index) => (
                        <motion.div
                          key={image.id}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          variants={fadeInUp}
                          transition={{ delay: index * 0.05 }}
                          className="relative aspect-square cursor-pointer group overflow-hidden rounded-lg"
                          onClick={() => setSelectedImage(image)}
                          data-testid={`gallery-image-${index}`}
                        >
                          <img
                            src={resolveImageUrl(image.image_url)}
                            alt={getLocalizedContent(image.title)}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                            <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity font-medium text-sm text-center px-2">
                              {getLocalizedContent(image.title)}
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </section>

      {/* Image Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden" data-testid="gallery-modal">
          {selectedImage && (
            <div>
              <img
                src={resolveImageUrl(selectedImage.image_url)}
                alt={getLocalizedContent(selectedImage.title)}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="p-4 bg-white">
                <h4 className="font-semibold text-[#0F172A]">
                  {getLocalizedContent(selectedImage.title)}
                </h4>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
